import React, { useId } from 'react'
import {
  Controller,
  type ControllerRenderProps,
  type FieldValues,
  type RegisterOptions,
  useFormContext,
} from 'react-hook-form'
import {
  colWidth,
  type FormCol,
  FormItemStatusContext,
  useFormLayout,
} from 'sutra/components/Form/form-layout'
import {
  ControlCell,
  FieldDescription,
  FieldError,
  type FormItemStatus,
  GroupRoot,
  ItemRoot,
  Label,
  LabelCell,
} from 'sutra/components/Form/form-styles'

// react-hook-form's rules, minus the ones that rewrite the stored value (a
// control's own `onChange` decides that) or disable the field.
export type FormRules = Omit<
  RegisterOptions<FieldValues, string>,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
>

export interface FormItemProps {
  // Binds the control to this field of the enclosing `Form`'s `form`. Without
  // it the item only lays out `label`, `help` and `extra` (a Formik form).
  name?: string
  label?: React.ReactNode
  rules?: FormRules
  // Replaces the validation message. With no `validateStatus` it reads as a
  // neutral hint rather than an error.
  help?: React.ReactNode
  // A neutral description under the control.
  extra?: React.ReactNode
  validateStatus?: FormItemStatus | ''
  // Shows the asterisk without a `required` rule.
  required?: boolean
  // The prop the control reads its value from (`checked` for a checkbox).
  valuePropName?: string
  // The prop the control calls with its next value (`onValueChange`).
  trigger?: string
  // Turns what `trigger` receives into the stored value; by default an event
  // yields `target.value` (`target.checked` for `valuePropName="checked"`).
  getValueFromEvent?: (...args: unknown[]) => unknown
  // The control is a set of controls (`RadioGroup`, `CheckboxGroup`) that name
  // themselves, so the item must not be one Base UI `Field` around them.
  group?: boolean
  // Binds the control without the label / message frame.
  noStyle?: boolean
  // The label's `for`, for an unnamed item whose control has an `id`.
  htmlFor?: string
  labelCol?: FormCol
  wrapperCol?: FormCol
  className?: string
  style?: React.CSSProperties
  'data-testid'?: string
  children?: React.ReactNode
}

function isEvent(value: unknown): value is { target: object } {
  return typeof value === 'object' && value !== null && 'target' in value
}

function valueFromArgs(
  args: unknown[],
  valuePropName: string,
  getValueFromEvent: FormItemProps['getValueFromEvent'],
): unknown {
  if (getValueFromEvent) return getValueFromEvent(...args)
  const [first] = args
  if (!isEvent(first)) return first
  const target = first.target as { value?: unknown; checked?: unknown }
  return valuePropName === 'checked' ? target.checked : target.value
}

function bindControl(
  child: React.ReactNode,
  field: ControllerRenderProps<FieldValues, string>,
  props: FormItemProps,
  id: string,
): React.ReactNode {
  if (!React.isValidElement<Record<string, unknown>>(child)) return child
  const { valuePropName = 'value', trigger = 'onChange' } = props
  const own = child.props[trigger]
  return React.cloneElement(child, {
    id: child.props['id'] ?? id,
    [valuePropName]:
      valuePropName === 'checked' ? Boolean(field.value) : field.value,
    [trigger]: (...args: unknown[]) => {
      field.onChange(
        valueFromArgs(args, valuePropName, props.getValueFromEvent),
      )
      if (typeof own === 'function') own(...args)
    },
  })
}

function resolveStatus(
  validateStatus: FormItemProps['validateStatus'],
  errorMessage: string | undefined,
): FormItemStatus | undefined {
  if (validateStatus === '') return undefined
  return validateStatus ?? (errorMessage ? 'error' : undefined)
}

interface FrameProps extends FormItemProps {
  errorMessage?: string
  touched?: boolean
  dirty?: boolean
  control: React.ReactNode
}

function Frame({
  label,
  name,
  rules,
  help,
  extra,
  validateStatus,
  required,
  group,
  htmlFor,
  labelCol,
  wrapperCol,
  className,
  style,
  'data-testid': testId,
  errorMessage,
  touched,
  dirty,
  control,
}: FrameProps): React.JSX.Element {
  const layout = useFormLayout()
  const status = resolveStatus(validateStatus, errorMessage)
  const message = validateStatus === '' ? help : (help ?? errorMessage)
  const hasMessage = message !== undefined && message !== null && message !== ''
  const isRequired = required ?? Boolean(rules?.required)
  const showMark = isRequired && layout.requiredMark
  const rows = (
    <>
      {label ? (
        <LabelCell
          $layout={layout.layout}
          $width={colWidth(labelCol ?? layout.labelCol)}
        >
          <Label htmlFor={htmlFor} data-required={showMark ? '' : undefined}>
            {label}
          </Label>
        </LabelCell>
      ) : null}
      <ControlCell
        $layout={layout.layout}
        $width={colWidth(wrapperCol ?? layout.wrapperCol)}
      >
        {control}
        {hasMessage && status ? (
          <FieldError match $status={status}>
            {message}
          </FieldError>
        ) : null}
        {hasMessage && !status ? (
          <FieldDescription>{message}</FieldDescription>
        ) : null}
        {extra ? <FieldDescription>{extra}</FieldDescription> : null}
      </ControlCell>
    </>
  )
  return (
    <FormItemStatusContext.Provider value={status}>
      {group ? (
        <GroupRoot
          $layout={layout.layout}
          className={className}
          style={style}
          data-testid={testId}
        >
          {rows}
        </GroupRoot>
      ) : (
        <ItemRoot
          $layout={layout.layout}
          name={name}
          invalid={status === 'error'}
          touched={touched}
          dirty={dirty}
          className={className}
          style={style}
          data-testid={testId}
        >
          {rows}
        </ItemRoot>
      )}
    </FormItemStatusContext.Provider>
  )
}

function BoundItem(props: FormItemProps & { name: string }): React.JSX.Element {
  const id = useId()
  const context = useFormContext()
  if (!context) {
    throw new Error(
      `FormItem name="${props.name}" needs a Form with a \`form\` (from useForm).`,
    )
  }
  return (
    <Controller
      control={context.control}
      name={props.name}
      rules={props.rules}
      render={({ field, fieldState }) => {
        const control = bindControl(props.children, field, props, id)
        if (props.noStyle) {
          return (
            <FormItemStatusContext.Provider
              value={resolveStatus(
                props.validateStatus,
                fieldState.error?.message,
              )}
            >
              {control}
            </FormItemStatusContext.Provider>
          )
        }
        return (
          <Frame
            {...props}
            htmlFor={props.htmlFor ?? id}
            errorMessage={fieldState.error?.message}
            touched={fieldState.isTouched}
            dirty={fieldState.isDirty}
            control={control}
          />
        )
      }}
    />
  )
}

// A labelled form row: `name` binds the control to the form's value (as antd's
// `Form.Item` did), and the label, validation message and description wire up
// through Base UI's `Field`.
export function FormItem(props: FormItemProps): React.JSX.Element {
  if (props.name !== undefined) {
    return <BoundItem {...props} name={props.name} />
  }
  if (props.noStyle) return <>{props.children}</>
  return <Frame {...props} control={props.children} />
}
